import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Loader2, RotateCcw, ArrowRight } from 'lucide-react';

export default function VerifyEmail() {
  const { t } = useLanguage();
  const { verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) {
      toast.error(t('auth.verify_enter_code'));
      return;
    }
    setLoading(true);
    try {
      await verifyEmail(email, code);
      toast.success(t('auth.verify_success'));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'verification failed';
      toast.error(detail);
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await resendCode(email);
      toast.success(t('auth.verify_resent'));
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  const code = digits.join('');

  return (
    <div className="min-h-screen bg-background" data-testid="verify-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-[10px] text-primary tracking-wider mb-4">$ auth --verify</div>
          <h1 className="text-xl font-bold tracking-tight mb-1">{t('auth.verify_title')}</h1>
          <p className="text-[11px] text-muted-foreground mb-2">{t('auth.verify_subtitle')}</p>
          <p className="font-mono text-xs text-foreground/80 mb-8 break-all" dir="ltr">→ {email}</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-center gap-1.5" dir="ltr" onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <Input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className="w-11 h-12 text-center text-lg font-mono font-bold bg-card border-border/50 rounded"
                  data-testid={`verify-digit-${i}`}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <Button
              type="submit"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-btn"
              className="w-full h-10 rounded text-[11px] tracking-wider"
            >
              {loading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <>{t('auth.verify_btn')} <ArrowRight className="ms-1.5 h-3 w-3" /></>}
            </Button>
          </form>

          <div className="text-center mt-6">
            <p className="text-[11px] text-muted-foreground mb-2">{t('auth.verify_no_code')}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resendLoading || cooldown > 0}
              className="gap-1.5 h-7 text-[11px] text-muted-foreground hover:text-primary"
              data-testid="verify-resend-btn"
            >
              <RotateCcw className="h-3 w-3" />
              {cooldown > 0
                ? `${t('auth.verify_resend')} (${cooldown}s)`
                : t('auth.verify_resend')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
